export const sortDueDateRowsByStatus = (rows = []) => {
	const statusOrder = { red: 1, yellow: 2 }

	return rows.slice().sort((a, b) => {
		const statusA = String(a?.status || '').toLowerCase()
		const statusB = String(b?.status || '').toLowerCase()
		const orderA = statusOrder[statusA] ?? Number.MAX_SAFE_INTEGER
		const orderB = statusOrder[statusB] ?? Number.MAX_SAFE_INTEGER

		return orderA - orderB || statusA.localeCompare(statusB)
	})
}

export const formatDueDateCountry = (value, rowData = {}) => {
	if (!value) {
		return '-'
	}

	let countryName = typeof value === 'object' ? value?.name : value
	if (typeof countryName === 'string' && countryName.includes(',')) {
		const parts = countryName.split(',')
		countryName = parts[parts.length - 1].trim()
	}

	const state = rowData?.state
	const isUSA = countryName === 'USA' || countryName === 'US' || countryName === 'United States'
	const isCanada = countryName === 'Canada' || countryName === 'CAN' || countryName === 'CA'

	if ((isUSA || isCanada) && state) {
		const countryCode = isUSA ? 'USA' : 'Canada'
		return `${state} (${countryCode})`
	}

	if (isUSA) {
		return 'United States'
	}

	if (isCanada) {
		return 'Canada'
	}

	return countryName
}

const getDueDateReportType = (rowData = {}) => {
	return String(rowData.type_of_report || rowData.name || '').toLowerCase()
}

export const isVatDueDate = (rowData = {}) => {
	const reportType = getDueDateReportType(rowData)
	return reportType.includes('vat return') || reportType.includes('vat')
}

export const isSalesTaxDueDate = (rowData = {}) => {
	return getDueDateReportType(rowData).includes('sales tax')
}

export const isEPRDueDate = (rowData = {}) => {
	return getDueDateReportType(rowData).includes('epr')
}

export const getDueDatePeriodTypeLabel = (rowData = {}, t, translationScope = 'home') => {
	const periodType = rowData.period_type

	if (isSalesTaxDueDate(rowData)) {
		switch (periodType) {
			case 'year':
				return t(`${translationScope}.ySalexTax`)
			case 'quarter':
				return t(`${translationScope}.qSalesTax`)
			case 'month':
				return t(`${translationScope}.mSalesTax`)
			case 'bi-annual':
				return 'Bi-Annual Sales tax'
			default:
				return periodType
		}
	}

	if (isEPRDueDate(rowData)) {
		switch (periodType) {
			case 'year':
				return t(`${translationScope}.yEPR`)
			case 'quarter':
				return t(`${translationScope}.qEPR`)
			case 'month':
				return t(`${translationScope}.mEPR`)
			default:
				return periodType
		}
	}

	switch (periodType) {
		case 'year':
			return t(`${translationScope}.yVAT`)
		case 'quarter':
			return t(`${translationScope}.qVAT`)
		case 'month':
			return t(`${translationScope}.mVAT`)
		case 'bi-annual':
			return 'Bi-Annual VAT'
		default:
			return periodType
	}
}

const buildVatDueDateState = (rowData = {}) => ({
	countryId: rowData.country_code || rowData.country_id || rowData.customer_country_id,
	countryName: typeof rowData.country === 'object' ? rowData.country?.name : rowData.country,
	period: rowData.period,
	periodType: rowData.period_type,
	year: rowData.period_year,
	reason: rowData.reason_for_registration_id,
})

const buildSalesTaxDueDateState = (rowData = {}) => ({
	stateId: rowData.state_code || rowData.country_code || rowData.state_id,
	stateName: typeof rowData.state === 'object' ? rowData.state?.name : rowData.state,
	period: rowData.period,
	periodType: rowData.period_type,
	year: rowData.period_year,
	taxNumber: rowData.tax_number,
})

const buildEPRDueDateState = (rowData = {}) => ({
	countryId: rowData.country_code || rowData.country_id || rowData.customer_country_id,
	countryName: typeof rowData.country === 'object' ? rowData.country?.name : rowData.country,
	period: rowData.period,
	periodType: rowData.period_type,
	year: rowData.period_year,
	reportType: rowData.epr_category || rowData.epr_category_id || rowData.report_type || rowData.type_of_report_code || '',
})

export const getDueDateDestinationRoute = (rowData = {}) => {
	if (isVatDueDate(rowData)) {
		return '/vat-returns/create-return'
	}

	if (isSalesTaxDueDate(rowData)) {
		return '/tax-returns/create-return'
	}

	if (isEPRDueDate(rowData)) {
		return '/epr-reporting/add'
	}

	return '/tax-returns'
}

export const buildDueDateLocationState = (rowData = {}) => {
	if (isVatDueDate(rowData)) {
		return buildVatDueDateState(rowData)
	}

	if (isSalesTaxDueDate(rowData)) {
		return buildSalesTaxDueDateState(rowData)
	}

	if (isEPRDueDate(rowData)) {
		return buildEPRDueDateState(rowData)
	}

	return undefined
}

export const getDueDateNavigation = (rowData = {}) => ({
	pathname: getDueDateDestinationRoute(rowData),
	state: buildDueDateLocationState(rowData),
})
